/**
 * Protocolo da transcrição entre o servidor e a sala (docs/specs/chamada-video-transcricao/TDD.md §5).
 *
 * O texto chega pelo canal de dados da sala. O que sustenta a confiança nele: o servidor de mídia GARANTE a
 * identidade de quem enviou, e só o nosso servidor emite token com identidade `tx:<videoCallId>`. Um convidado
 * consegue publicar no mesmo canal (e o texto chega, com a identidade DELE), então quem aceita a mensagem é só
 * quem tem identidade `tx:`.
 *
 * Os valores abaixo repetem `packages/domain/videoCall/application/transcription/transcriptionConstants.js`:
 * mudar um é mudar os dois.
 */

export const TRANSCRIPT_TOPIC = 'touch.transcript';
/** Atributo assinado no token de quem aceitou a transcrição (vale para o atendente numa chamada com a Jev). */
export const TRANSCRIPTION_ATTRIBUTE = 'transcription';
export const TRANSCRIPTION_ON = 'on';

const TRANSCRIBER_PREFIX = 'tx:';
const ROLES = new Set(['AGENT', 'GUEST', 'APP_CONTACT']);
/** Uma fala maior que isso não é fala: descarta em vez de estourar a tela. */
const MAX_TEXT = 4000;

/** @param {unknown} identity */
export const isTranscriberIdentity = (identity) => typeof identity === 'string' && identity.startsWith(TRANSCRIBER_PREFIX);

/** A pessoa aceitou a transcrição (atributo assinado no token dela)? @param {{ attributes?: Record<string, string> }|undefined} participant */
export const isTranscribed = (participant) => participant?.attributes?.[TRANSCRIPTION_ATTRIBUTE] === TRANSCRIPTION_ON;

/**
 * @typedef {{
 *   id: string, participantId: string, identity: string, name: string, role: string,
 *   startedAt: number, endedAt: number, text: string,
 * }} Segment
 */

const isText = (value) => typeof value === 'string' && value.length > 0;
const isTime = (value) => Number.isFinite(value) && value > 0;

/**
 * Valida uma fala vinda do servidor (REST ou canal de dados). Devolve só os campos conhecidos, ou `null`.
 * @param {unknown} raw
 * @returns {Segment|null}
 */
export function normalizeSegment(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const s = /** @type {Record<string, unknown>} */ (raw);
    if (!isText(s.id) || !isText(s.participantId) || !isText(s.identity) || !isText(s.name)) return null;
    if (!ROLES.has(/** @type {string} */ (s.role))) return null;
    if (!isTime(s.startedAt) || !isTime(s.endedAt)) return null;
    if (!isText(s.text) || s.text.length > MAX_TEXT) return null;
    return {
        id: s.id,
        participantId: s.participantId,
        identity: s.identity,
        name: s.name,
        role: /** @type {string} */ (s.role),
        startedAt: /** @type {number} */ (s.startedAt),
        endedAt: /** @type {number} */ (s.endedAt),
        text: s.text,
    };
}

/**
 * Mensagem do canal de dados → fala, só se veio do transcritor.
 * @param {{ payload: Uint8Array, participant?: { identity?: string }, topic?: string }} message
 * @returns {Segment|null}
 */
export function parseTranscriptMessage({ payload, participant, topic }) {
    if (topic !== TRANSCRIPT_TOPIC) return null;
    if (!isTranscriberIdentity(participant?.identity)) return null;
    try {
        const body = JSON.parse(new TextDecoder().decode(payload));
        if (body?.v !== 1 || body?.type !== 'segment') return null;
        return normalizeSegment(body);
    } catch {
        return null;
    }
}

/**
 * Junta falas novas às que já existem, sem duplicar (mesmo `id`) e em ordem de início. Devolve o MESMO array
 * quando nada mudou (não re-renderiza à toa).
 * @param {Segment[]} existing @param {Segment[]} incoming
 */
export function mergeSegments(existing, incoming) {
    const byId = new Map(existing.map((s) => [s.id, s]));
    let changed = false;
    for (const segment of incoming) {
        const current = byId.get(segment.id);
        if (!current || current.text !== segment.text || current.endedAt !== segment.endedAt) {
            byId.set(segment.id, segment);
            changed = true;
        }
    }
    if (!changed) return existing;
    return [...byId.values()].sort((a, b) => a.startedAt - b.startedAt || a.id.localeCompare(b.id));
}

/** "mm:ss" (ou "h:mm:ss") do deslocamento de `atMs` em relação à origem da chamada (o mesmo relógio do cronômetro). */
export function formatOffset(atMs, originMs) {
    const total = Math.max(0, Math.floor(((originMs == null ? atMs : atMs - originMs)) / 1000));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const pad = (n) => String(n).padStart(2, '0');
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(total % 60)}` : `${pad(minutes)}:${pad(total % 60)}`;
}

/** Sem origem da chamada (ainda não começou), o horário do relógio, em Brasília. */
export function formatClock(atMs) {
    return new Date(atMs).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
}

/** O rótulo de tempo de uma fala ou momento: o deslocamento da chamada, ou o relógio se ela ainda não tem origem. */
export const timeLabel = (atMs, originMs) => (originMs == null ? formatClock(atMs) : formatOffset(atMs, originMs));

/**
 * Falas seguidas da mesma pessoa, com pouca pausa entre elas, viram um turno só (como numa conversa escrita).
 * @param {Segment[]} segments em ordem de início
 * @param {{ maxGapMs?: number }} [options]
 * @returns {Array<Segment & { key: string, segmentIds: string[] }>}
 */
export function groupTurns(segments, { maxGapMs = 4000 } = {}) {
    const turns = [];
    for (const segment of segments) {
        const last = turns[turns.length - 1];
        if (last && last.identity === segment.identity && segment.startedAt - last.endedAt <= maxGapMs) {
            last.text = `${last.text} ${segment.text}`;
            last.endedAt = Math.max(last.endedAt, segment.endedAt);
            last.segmentIds.push(segment.id);
        } else {
            turns.push({ ...segment, key: segment.id, segmentIds: [segment.id] });
        }
    }
    return turns;
}

/** Sem acento e em minúsculas: "certidão" acha "CERTIDAO". */
export const normalizeForSearch = (text) => String(text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Parte o texto nos pedaços que casam com a busca (para destacar), sem se confundir com acentos.
 * @returns {Array<{ text: string, match: boolean }>}
 */
export function splitByMatches(text, query) {
    const needle = normalizeForSearch(query).trim();
    if (!needle) return [{ text, match: false }];
    // `normalize('NFD')` muda o tamanho do texto: mapeia cada letra do normalizado à posição no original.
    const original = Array.from(text);
    let normalized = '';
    const origin = [];
    original.forEach((char, index) => {
        const folded = normalizeForSearch(char);
        for (const _ of folded) origin.push(index);
        normalized += folded;
    });
    const parts = [];
    let from = 0;
    let at = normalized.indexOf(needle);
    while (at !== -1) {
        const start = origin[at];
        const end = origin[at + needle.length - 1] + 1;
        if (start > from) parts.push({ text: original.slice(from, start).join(''), match: false });
        parts.push({ text: original.slice(start, end).join(''), match: true });
        from = end;
        at = normalized.indexOf(needle, at + needle.length);
    }
    if (from < original.length) parts.push({ text: original.slice(from).join(''), match: false });
    return parts.length ? parts : [{ text, match: false }];
}

/** @param {Segment[]} segments @param {string} query */
export function filterSegments(segments, query) {
    const needle = normalizeForSearch(query).trim();
    if (!needle) return segments;
    return segments.filter((s) => normalizeForSearch(s.text).includes(needle));
}

/** A fala que estava acontecendo (ou acabou de acontecer) no momento marcado: o contexto do marcador. */
export function segmentAt(segments, atMs) {
    let found = null;
    for (const segment of segments) {
        if (segment.startedAt <= atMs) found = segment;
        else break;
    }
    return found;
}
